import Service from 'react-reservice';

import {
  EAqxTraderThemeMode,
  ICreateThemeColorsParams,
  IFontFamily,
  createTheme
} from '@aquariuxfintech/aqx-trader-sdk-config/theme';

import configs from '@aqx-core/common/configs';
import { profitLossChartViewController } from '@aqx-core/common/services/asset/profit-loss-chart/controller';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import { companyService } from '@aqx-core/common/services/company/service';
import { getThemeTemplate } from '@aqx-core/common/services/company/utils';
import { equityChartViewController } from '@aqx-core/common/services/trade/analysis/equity-chart/controller';
import storage, { EStorageKey } from '@aqx-core/common/storage';

import { DEFAULT_CANDLE_STICK_COLORS_RGB } from '../colors';
import { EAppThemeMode, EAppThemeScheme, ITenantColors } from '../types';
import { formatTenantColors, getSystemColorMode, getThemeMode, hexToRgb } from '../utils';
import { TENANT_CUSTOM_THEME_TEMPLATE_MAP } from './constants';
import { appThemeService } from './service';

class AppThemeViewController extends Service {
  private isSkipSetThemeToStorage = false;
  constructor() {
    super();
  }

  init = (themeMode: EAppThemeMode) => {
    const themeScheme: EAppThemeScheme | undefined = storage.getItem(EStorageKey.THEME_SCHEME);
    if (storage.getItem(EStorageKey.THEME_SCHEME) === undefined) {
      this.isSkipSetThemeToStorage = true;
    }
    // Get default theme when no theme scheme is set to the storage
    if (!themeScheme) {
      this.updateTheme(appThemeService.context.themeScheme, appThemeService.context.theme.themeMode);
      return;
    }

    const formattedThemeMode = themeScheme === EAppThemeScheme.SYSTEM_SCHEME ? themeMode : getThemeMode(themeScheme);
    this.updateTheme(themeScheme, formattedThemeMode);
    this.setDeviceThemeMode(themeMode);
  };

  setDeviceThemeMode = (nextDeviceThemeMode: EAppThemeMode) => {
    appThemeService.setDeviceThemeMode(nextDeviceThemeMode);
  };

  updateTheme = (nextThemeScheme: EAppThemeScheme, nextThemeMode: EAppThemeMode) => {
    this.setThemeScheme(nextThemeScheme);
    this.setThemeMode(nextThemeMode);
    equityChartViewController.updateChartTheme(nextThemeMode);
    profitLossChartViewController.updateChartTheme();
  };

  setThemeScheme = (nextThemeScheme: EAppThemeScheme) => {
    if (!this.isSkipSetThemeToStorage) {
      storage.setItem(EStorageKey.THEME_SCHEME, nextThemeScheme);
    }
    appThemeService.setThemeScheme(nextThemeScheme);
  };

  setThemeMode = (nextThemeMode: EAppThemeMode) => {
    const {
      tenantId = '',
      themeColourCode,
      profitColourCode,
      lossColourCode
    } = companyViewController.getTenantIdAndColors();

    const nextThemeScheme =
      nextThemeMode === EAppThemeMode.DARK ? EAppThemeScheme.DARK_SCHEME : EAppThemeScheme.LIGHT_SCHEME;

    this.setThemeByTenant(
      tenantId,
      formatTenantColors({ themeColourCode, profitColourCode, lossColourCode }),
      nextThemeScheme
    );
  };

  setFontFamily = (nextFontFamily: IFontFamily) => {
    appThemeService.setFontFamily(nextFontFamily);
  };

  private getTenantAppTheme = (
    _tenantId: string
  ): {
    tenantDarkThemeColors: ICreateThemeColorsParams;
    tenantLightThemeColors: ICreateThemeColorsParams;
    fontFamily: IFontFamily;
    defaultThemeScheme: EAppThemeScheme | undefined;
  } => {
    const tenantId = _tenantId || companyService.context.companyProfile?.tenantId;
    const themeTemplate = getThemeTemplate(tenantId, configs.environment);

    return TENANT_CUSTOM_THEME_TEMPLATE_MAP[themeTemplate];
  };

  setThemeByTenant = (tenantId: string, tenantColors: ITenantColors, nextThemeScheme?: EAppThemeScheme) => {
    const {
      tenantDarkThemeColors,
      tenantLightThemeColors,
      fontFamily,
      defaultThemeScheme = appThemeService.context.themeScheme
    } = this.getTenantAppTheme(tenantId);

    const { primary, positive, negative } = tenantColors || {};
    const themeScheme = nextThemeScheme ?? storage.getItem(EStorageKey.THEME_SCHEME) ?? defaultThemeScheme;

    if (this.isSkipSetThemeToStorage && tenantId) {
      this.isSkipSetThemeToStorage = false;
      this.setThemeScheme(themeScheme);
      storage.setItem(EStorageKey.THEME_SCHEME, themeScheme);
    }
    const themeMode = this.getThemeModeFromThemeScheme(themeScheme);

    if (themeMode === EAppThemeMode.DARK) {
      appThemeService.setThemeModeAndColors({
        themeMode: EAppThemeMode.DARK,
        themeColors: createTheme({
          themeMode: EAqxTraderThemeMode.DARK,
          themeColors: {
            ...tenantDarkThemeColors,
            primary,
            pnl: { positive: { default: positive }, negative: { default: negative } }
          }
        }).themeColors
      });
    } else {
      appThemeService.setThemeModeAndColors({
        themeMode: EAppThemeMode.LIGHT,
        themeColors: createTheme({
          themeMode: EAqxTraderThemeMode.LIGHT,
          themeColors: {
            ...tenantLightThemeColors,
            primary,
            pnl: { positive: { default: positive }, negative: { default: negative } }
          }
        }).themeColors
      });
    }

    appThemeService.setFontFamily(fontFamily);
  };

  private getThemeModeFromThemeScheme = (themeScheme: EAppThemeScheme): EAppThemeMode => {
    if (themeScheme === EAppThemeScheme.DARK_SCHEME) {
      return EAppThemeMode.DARK;
    }
    if (themeScheme === EAppThemeScheme.LIGHT_SCHEME) {
      return EAppThemeMode.LIGHT;
    }

    return getSystemColorMode();
  };

  setPrimaryColor = (nextPrimaryColor: string) => {
    const rgbColor = hexToRgb(nextPrimaryColor);
    if (rgbColor) {
      appThemeService.setPrimaryColor(rgbColor);
    }
  };

  setDefaultCandlestickColors = (
    positive: string = DEFAULT_CANDLE_STICK_COLORS_RGB.positive,
    negative: string = DEFAULT_CANDLE_STICK_COLORS_RGB.negative
  ) => {
    const candlestick = {
      positive: hexToRgb(positive) || positive,
      negative: hexToRgb(negative) || negative
    };

    appThemeService.setCandlestickColors(candlestick);
  };

  getThemeColors = () => {
    return appThemeService.getThemeColors();
  };
}

export const appThemeViewController = new AppThemeViewController();
